import { GetStoreMetadata } from '@/data';
import type {
  ProductListingPageDocumentData,
  ProductListingPageDocumentDataPrioritizedBrandsItem,
  StrainPageDocumentDataPrioritizedBrandsItem,
  SubcategoryPageDocumentDataPrioritizedBrandsItem,
} from 'prismicio-types';
import {
  ProductListingPageDocument,
  StrainPageDocument,
  SubcategoryPageDocument,
} from '@/types';

/**
 * Rows in `prioritized_brands` — same shape on category (`product_listing_page`),
 * strain (`strain_page`), and subcategory (`subcategory_page`).
 */
export type PrioritizedBrandsRow =
  | ProductListingPageDocumentDataPrioritizedBrandsItem
  | StrainPageDocumentDataPrioritizedBrandsItem
  | SubcategoryPageDocumentDataPrioritizedBrandsItem;

/**
 * Prismic field bundle for product prioritization (same API IDs on the three shop listing types above).
 */
export type PrioritizationFields = Pick<
  ProductListingPageDocumentData,
  'enable_product_prioritization' | 'prioritized_brands'
>;

/**
 * Resolved Prismic prioritization for a shop listing document or inherited default/settings.
 * When `enabled`, that route injects prioritization into the OpenSearch `sort` (brand script + field sort)
 * only while the listing uses its Prismic default sort—not after the user picks another sort.
 */
export type ResolvedPrioritization =
  | {
      enabled: true;
      /** Canonical brand.keyword -> priority rank (1 = first). */
      brandPriorityMap: Record<string, number>;
    }
  | { enabled: false };

/**
 * `enabled: true` after resolving Prismic fields on the current shop page (category / strain / subcategory)
 * or fallback chain. OpenSearch carries the sort for that listing's queries only.
 */
export const isListingPrioritizationEnabled = (
  prioritization: ResolvedPrioritization | undefined,
): prioritization is Extract<ResolvedPrioritization, { enabled: true }> =>
  Boolean(prioritization && prioritization.enabled);

/** Shop listing Prismic types that define prioritization fields. */
type ShopListingDocument =
  | ProductListingPageDocument
  | StrainPageDocument
  | SubcategoryPageDocument
  | null;

export type ResolvedShopListingDocument = NonNullable<ShopListingDocument>;

export const canonicalizeBrandKeyForOpenSearch = (raw: string): string =>
  raw.trim().replace(/\s+/g, '_').toUpperCase();

/**
 * Rank follows Prismic repeat order: first row with a valid `brand_name` → 1, next distinct brand → 2, …
 * Rows without a name are skipped (no gap). Duplicate canonical keys keep the rank from their first row.
 */
const parseBrandRows = (
  rows: readonly PrioritizedBrandsRow[] | undefined | null,
): Record<string, number> => {
  if (!rows?.length) return {};

  const map: Record<string, number> = {};
  let rank = 1;
  for (const row of rows) {
    const name = row.brand_name?.trim();
    if (!name) continue;
    const key = canonicalizeBrandKeyForOpenSearch(name);
    if (map[key] !== undefined) continue;
    map[key] = rank;
    rank += 1;
  }
  return map;
};

export const resolvePrioritizationFromFields = (
  fields: PrioritizationFields | undefined | null,
): ResolvedPrioritization => {
  if (!fields?.enable_product_prioritization) {
    return { enabled: false };
  }

  const brandPriorityMap = parseBrandRows(
    fields.prioritized_brands ?? undefined,
  );
  if (!Object.keys(brandPriorityMap).length) {
    return { enabled: false };
  }
  return { enabled: true, brandPriorityMap };
};

export const getPrioritizationWithFallback = async (
  document?: ShopListingDocument,
  defaultDocument?: ShopListingDocument,
): Promise<ResolvedPrioritization> => {
  const pageData = document?.data;
  // Explicit OFF on this page: do not inherit from default/settings.
  if (pageData && pageData.enable_product_prioritization === false) {
    return { enabled: false };
  }

  const primary = resolvePrioritizationFromFields(pageData);
  if (primary.enabled) return primary;

  const secondary = resolvePrioritizationFromFields(defaultDocument?.data);
  if (secondary.enabled) return secondary;

  const { settings } = await GetStoreMetadata();
  const tertiary = resolvePrioritizationFromFields(
    settings as unknown as PrioritizationFields | undefined,
  );
  return tertiary;
};

export type ResolvedShopPagePrioritization = Awaited<
  ReturnType<typeof getPrioritizationWithFallback>
>;

/**
 * Used by storefront shop listing pages after `Promise.allSettled` Prismic fetches:
 * uses the UID document when fulfilled, otherwise the default document, then getPrioritizationWithFallback.
 */
export const getPrioritizationForListingDocuments = async (
  documentRes: PromiseSettledResult<ResolvedShopListingDocument>,
  defaultDocument: ResolvedShopListingDocument,
): Promise<ResolvedPrioritization> => {
  const pageDocument =
    documentRes.status === 'fulfilled' && documentRes.value
      ? documentRes.value
      : defaultDocument;

  return getPrioritizationWithFallback(pageDocument, defaultDocument);
};
